/**
 * The pause menu's controls list (08 §2.8), built from the ACTIVE scheme's bindings so it shows
 * the keys the player actually has, not a table somebody typed in once. Pure.
 */

import { keyLabel, keysLabel } from '../../input/bindings';
import type { BindingsPort } from '../../input/bindings';

export type ControlRow = [keys: string, action: string];

/** What the list shows when the HUD was given no bindings: the modern defaults. */
export const DEFAULT_CONTROLS: readonly ControlRow[] = [
  ['WASD', 'MOVE'],
  ['MOUSE', 'AIM'],
  ['LMB', 'FIRE'],
  ['RMB', 'USE ITEM / HOLD TO THROW'],
  ['SHIFT', 'RUN'],
  ['C', 'CRAWL'],
  ['X', 'SNEAK'],
  ['1-5', 'WEAPONS'],
  ['R', 'RELOAD'],
  ['F', 'FIRST AID'],
  ['E', 'INTERACT'],
  ['HOLD G', 'RECALL OSPREY'],
  ['ESC', 'PAUSE'],
];

type Actions = ReturnType<BindingsPort['actions']>;

function first(actions: Actions, id: string): string | null {
  const code = actions.find((a) => a.id === id)?.keys[0];
  return code === undefined ? null : keyLabel(code);
}

function all(actions: Actions, id: string): string {
  return keysLabel(actions.find((a) => a.id === id)?.keys ?? []);
}

/** `WASD` when the four keys spell it, `ARROWS` for the arrows, else the four labels in a row. */
function moveKeys(actions: Actions): string {
  const keys = ['moveUp', 'moveLeft', 'moveDown', 'moveRight'].map((id) => first(actions, id) ?? '-');
  const joined = keys.join('');
  if (joined === 'WASD') return 'WASD';
  if (joined === 'UPLEFTDOWNRIGHT') return 'ARROWS';
  return keys.join(' ');
}

/** `1-5` when the weapon keys run in sequence, else the labels in a row. */
function weaponKeys(actions: Actions): string {
  const keys = ['weapon1', 'weapon2', 'weapon3', 'weapon4', 'weapon5'].map((id) => first(actions, id) ?? '-');
  const digits = keys.map(Number);
  const run = digits.every((d, i) => Number.isInteger(d) && d === (digits[0] ?? 0) + i);
  return run ? `${keys[0]}-${keys[4]}` : keys.join(' ');
}

/** The rows of the pause menu's controls list for the port's active scheme. */
export function controlsList(port: BindingsPort | undefined): ControlRow[] {
  if (port === undefined) return DEFAULT_CONTROLS.map(([keys, action]): ControlRow => [keys, action]);
  const actions = port.actions();
  const classic = port.scheme() === 'classic';
  const rows: ControlRow[] = [
    [moveKeys(actions), classic ? 'MOVE / TAP TO TURN' : 'MOVE'],
    [classic ? '32-STEP' : 'MOUSE', classic ? 'AIM FOLLOWS FACING' : 'AIM'],
    [all(actions, 'fire'), 'FIRE'],
    [all(actions, 'use'), 'USE ITEM / THROW'],
    [all(actions, 'run'), 'RUN'],
    [all(actions, 'crawl'), 'CRAWL'],
    // ROUND7: the crouched walk. Always listed, like crawl: a dash says a player's old bindings left it no key.
    [all(actions, 'sneak'), 'SNEAK'],
    [weaponKeys(actions), 'WEAPONS'],
    [`${all(actions, 'prevWeapon')} ${all(actions, 'nextWeapon')}`, 'PREV / NEXT WEAPON'],
    [all(actions, 'reload'), 'RELOAD'],
    [all(actions, 'firstAid'), 'FIRST AID'],
    [all(actions, 'interact'), 'INTERACT'],
    [['fuse5', 'fuse10', 'fuse15'].map((id) => first(actions, id) ?? '-').join(' '), 'FUSE 5 / 10 / 15 S'],
    [`HOLD ${all(actions, 'recall')}`, 'RECALL OSPREY'],
    [all(actions, 'pause'), 'PAUSE'],
  ];
  // Unbound extras are left out rather than listed with a dash.
  const knife = first(actions, 'quickKnife');
  const afterCycle = rows.findIndex(([, action]) => action === 'PREV / NEXT WEAPON') + 1;
  if (knife !== null) rows.splice(afterCycle, 0, [all(actions, 'quickKnife'), 'QUICK KNIFE']);
  return rows;
}
